import React, { useState, useEffect, useCallback } from 'react';
import { Dialog } from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ButtonComponent } from 'component/Button';
import { SelectComponent } from 'component/Select';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Table, TableHeader, TableRow } from 'component/Table';
import { CheckboxInput } from 'component/Checkbox';
import { Loader } from 'component/Loader';
import { ClipLoader } from 'react-spinners';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { ReactComponent as SendSvg } from 'assets/images/icons/send.svg';
import { ReactComponent as AddSvg } from 'assets/images/icons/add_24px.svg';
import 'assets/styles/popup.scss';
import '../../Weekly/createPopup.scss';

const CreateReportPopup = ({ type, onCreateReport, disabled = false, customStyles, menuPortalTarget }) => {
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedTcpData, setSelectedTcpData] = useState(null);
  const [selectedHaData, setSelectedHaData] = useState(null);
  const [tcpInputValue, setTcpInputValue] = useState('');
  const [haInputValue, setHaInputValue] = useState('');
  const [allowedReports, setAllowedReports] = useState([]);
  const [selectedReports, setSelectedReports] = useState([]);
  const [loader, setLoader] = useState(false);
  const [hasExpandedOnce, setHasExpandedOnce] = useState(false);



  const reportTypeOptions = [
    { value: 'TCP', label: 'TCP' },
    { value: 'HA', label: 'HA' },
    { value: 'Empty', label: 'Empty' }
  ];

  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      return new Promise((resolve) => {
        timeoutId = setTimeout(async () => {
          const result = await func(...args);
          resolve(result);
        }, delay);
      });
    };
  };

  const searchClearanceTaskBase = async (searchTerm, loadedOptions, { page }, type) => {
    if (!searchTerm || searchTerm.length < 1) return {
      options: [],
      hasMore: false,
      additional: { page: 1 }
    };

    try {
      const params = {
        search: searchTerm,
      };

      if (type !== 'daily' && type !== 'weekly') {
        params.global_search = true;
      }

      const response = await axios.get('/reports/clearance/clearance-tasks/lookup/', { params });

      const dataArray = Array.isArray(response.data) ? response.data : [];
      const newOptions = dataArray.map(item => ({
        value: item.value,
        label: item.label
      }));

      return {
        options: newOptions,
        hasMore: false,
        additional: { page: 1 }
      };
    } catch (error) {
      console.error('Error searching clearance tasks:', error);
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 }
      };
    }
  };

  const searchClearanceTask = useCallback((...args) => searchClearanceTaskBase(...args, type), [type]);

  const searchHAReportsBase = async (searchTerm, loadedOptions, { page }) => {
    if (!searchTerm || searchTerm.length < 1) return {
      options: [],
      hasMore: false,
      additional: { page: 1 }
    };

    try {
      const response = await axios.get('/reports/ha/lookup/', {
        params: {
          search: searchTerm,
          global_search: true,
          page: page || 1
        }
      });

      const newOptions = response?.data?.results?.map(item => ({
        value: item.value,
        label: item.label
      })) || [];

      return {
        options: newOptions,
        hasMore: !!response?.data?.next,
        additional: { page: (page || 1) + 1 }
      };
    } catch (error) {
      console.error('Error searching HA reports:', error);
      return {
        options: [],
        hasMore: false,
        additional: { page: 1 }
      };
    }
  };

  const searchHAReports = useCallback(searchHAReportsBase, []);

  const handleClose = () => {
    setOpen(false);
    setSelectedType('');
    setSelectedTcpData(null);
    setSelectedHaData(null);
    setTcpInputValue('');
    setHaInputValue('');
    setAllowedReports([]);
    setSelectedReports([]);
    setHasExpandedOnce(false);
  };

  const handleTypeChange = (value) => {
    setSelectedType(value);
    if (value === 'TCP') {
      setSelectedTcpData(null);
      setTcpInputValue('');
    } else if (value === 'HA') {
      setSelectedHaData(null);
      setHaInputValue('');
    }
    setAllowedReports([]);
    setSelectedReports([]);
  };

  const currentSelectedData = selectedType === 'TCP' ? selectedTcpData : selectedHaData;
  const currentSelectedId = currentSelectedData?.value;

        const fetchAllowedReports = async (tcpId) => {
    setLoader(true);
    setAllowedReports([]);

    try {
      const response = await axios.get('/reports/clearance/daily/for-weekly/', {
        params: {
          clearance_task: tcpId
        }
      });

      const dailyReports = response?.data?.results || response?.data || [];
      setAllowedReports(dailyReports);
      setLoader(false);
    } catch (error) {
      toast.error('Error loading allowed reports');
      console.error('Error fetching allowed reports:', error);
      setLoader(false);
    }
  };

  const handleCheckboxChange = (reportId) => {
    setSelectedReports(prev => {
      if (prev.includes(reportId)) {
        return prev.filter(id => id !== reportId);
      }
      return [...prev, reportId];
    });
  };

  const isWeeklyType = type === 'weekly';
  const shouldShowSubTypeSelect = selectedType === 'TCP' || selectedType === 'HA';
  const isCreateButtonEnabled = selectedType === 'Empty' || (shouldShowSubTypeSelect && currentSelectedId);

  useEffect(() => {
    if (isWeeklyType && selectedTcpData?.value) {
      setHasExpandedOnce(true);
      fetchAllowedReports(selectedTcpData.value);
    }
  }, [isWeeklyType, selectedTcpData]);

  const shouldShowFirstSelect = !isWeeklyType;
  const shouldShowSecondSelect = isWeeklyType || shouldShowSubTypeSelect;
  const shouldShowReportsList = isWeeklyType && selectedTcpData?.value;

  const weeklyCreateButtonEnabled = isWeeklyType
    ? (allowedReports.length > 0 ? selectedReports.length > 0 : false)
    : isCreateButtonEnabled;



  const handleCreate = async () => {
    if (!weeklyCreateButtonEnabled) {
      return;
    }

    let success = false;

    try {
      if (isWeeklyType && shouldShowReportsList && selectedReports.length > 0) {
        if (selectedReports.length === 0) {
          toast.warning('Please select at least one report');
          return;
        }

        const reportData = {
          type: 'weekly',
          tcpId: selectedTcpData?.value,
          dailyReportIds: selectedReports
        };

        success = await onCreateReport(reportData);
      } else {
        const reportData = {
          type: selectedType || (isWeeklyType ? 'weekly' : ''),
          subType: currentSelectedId || null
        };

        success = await onCreateReport(reportData);
      }
    } catch (error) {
      console.error('Error in handleCreate:', error);
      success = false;
    }

    if (success) {
      handleClose();
    }
  };

  return (
    <>
      <ButtonComponent
        iconLeft={<AddSvg />}
        label={`Create ${type}`}
        handleButton={() => setOpen(true)}
        disabled={disabled}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="create-report-dialog-title"
        aria-describedby="create-report-dialog-description"
        className="weekly-report-dialog"
        maxWidth={false}
        PaperProps={{
          style: {
            width: (shouldShowReportsList || hasExpandedOnce) ? '1200px' : '380px',
            height: (shouldShowReportsList || hasExpandedOnce) ? '600px' : 'auto',
            maxHeight: (shouldShowReportsList || hasExpandedOnce) ? '600px' : '380px',
            borderRadius: '24px',
            transition: 'width 0.3s ease, height 0.3s ease',
            overflow: 'hidden'
          }
        }}
      >
        <div className="confirm-popup" style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div className="header">
            <span className="title">Create report</span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {shouldShowFirstSelect && (
              <>
                <div>
                  <span style={{
                    color: '#868686',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Montserrat-Medium'
                  }}>
                    Select report type
                  </span>
                </div>

                <SelectComponent
                  id="report-type-select"
                  name="report-type"
                  options={reportTypeOptions}
                  value={selectedType ? { value: selectedType, label: selectedType } : null}
                  handleSelect={(value, label) => handleTypeChange(value)}
                  placeholder="Select report type..."
                  customStyles={customStyles}
                  menuPortalTarget={menuPortalTarget}
                />
              </>
            )}

            {shouldShowSecondSelect && (
              <div style={{ marginTop: shouldShowFirstSelect ? '10px' : '0' }}>
                <div >
                  <span style={{
                    color: '#868686',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Montserrat-Medium'
                  }}>
                    {isWeeklyType ? 'Enter TCP imsma id' : `Enter ${selectedType} imsma id`}
                  </span>
                </div>
                {isWeeklyType ? (
                  <AsyncPaginate
                    loadOptions={searchClearanceTask}
                    value={selectedTcpData}
                    inputValue={tcpInputValue}
                    onInputChange={(newValue) => setTcpInputValue(newValue)}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        setSelectedTcpData(selectedOption);
                        setTcpInputValue(selectedOption.label);
                      } else {
                        setSelectedTcpData(null);
                        setTcpInputValue('');
                        setHasExpandedOnce(false);
                        setAllowedReports([]);
                        setSelectedReports([]);
                      }
                    }}
                    placeholder="Enter TCP imsma id..."
                    styles={customStyles}
                    menuPortalTarget={menuPortalTarget}
                    noOptionsMessage={() => 'Enter TCP imsma id'}
                    additional={{ page: 1 }}
                    className="custom-select-container"
                    isSearchable
                    isClearable
                    closeMenuOnSelect={true}
                    blurInputOnSelect={true}
                    minInputLength={0}
                    debounceTimeout={300}
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 0,
                      colors: {
                        ...theme.colors,
                        primary25: '#DFDFDF',
                        primary: '#00B1B7',
                      },
                    })}
                  />
                ) : (
                  <AsyncPaginate
                    loadOptions={selectedType === 'TCP' ? searchClearanceTask : searchHAReports}
                    value={currentSelectedData}
                    inputValue={selectedType === 'TCP' ? tcpInputValue : haInputValue}
                    onInputChange={(newValue) => {
                      if (selectedType === 'TCP') {
                        setTcpInputValue(newValue);
                      } else {
                        setHaInputValue(newValue);
                      }
                    }}
                    onChange={(selectedOption) => {
                      if (selectedOption) {
                        if (selectedType === 'TCP') {
                          setSelectedTcpData(selectedOption);
                          setTcpInputValue(selectedOption.label);
                        } else {
                          setSelectedHaData(selectedOption);
                          setHaInputValue(selectedOption.label);
                        }
                      } else {
                        if (selectedType === 'TCP') {
                          setSelectedTcpData(null);
                          setTcpInputValue('');
                        } else {
                          setSelectedHaData(null);
                          setHaInputValue('');
                        }
                      }
                    }}
                    placeholder={`Enter ${selectedType} imsma id...`}
                    styles={customStyles}
                    menuPortalTarget={menuPortalTarget}
                    noOptionsMessage={() => `Enter ${selectedType} imsma id`}
                    additional={{ page: 1 }}
                    className="custom-select-container"
                    isSearchable
                    isClearable
                    closeMenuOnSelect={true}
                    blurInputOnSelect={true}
                    minInputLength={0}
                    debounceTimeout={300}
                    theme={(theme) => ({
                      ...theme,
                      borderRadius: 0,
                      colors: {
                        ...theme.colors,
                        primary25: '#DFDFDF',
                        primary: '#00B1B7',
                      },
                    })}
                  />
                )}
              </div>
            )}

            {shouldShowReportsList && (
              <div style={{ marginTop: '10px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div >
                  <span style={{
                    color: '#868686',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'Montserrat-Medium'
                  }}>
                    Select daily reports
                  </span>
                </div>
                <div className="table-container" style={{ flex: 1, overflow: 'hidden',paddingBottom:'16px' }}>
                  <Table>
                    <TableHeader>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Team</th>
                      <th>Task ID</th>
                    </TableHeader>
                    <tbody className="scrollable-tbody">
                      {loader && (
                        <TableRow>
                          <td colSpan={4} style={{ textAlign: 'center', padding: '10px' }}>
                            <ClipLoader
                              color="#36d7b7"
                              loading={loader}
                              size={30}
                            />
                            <div style={{ marginTop: '10px',marginLeft: '10px', color: '#868686' }}>
                              Завантаження звітів...
                            </div>
                          </td>
                        </TableRow>
                      )}
                      {!loader && allowedReports?.length === 0 && (
                        <TableRow>
                          <td colSpan={4}>Дані відсутні</td>
                        </TableRow>
                      )}
                      {!loader && allowedReports?.map((report) => (
                        <TableRow key={report?.id}>
                          <td style={{
                            display: 'flex',
                            alignItems: 'start',
                          }}>
                            <CheckboxInput
                              id={`report-${report.id}`}
                              name={`report-${report.id}`}
                              value={report.id}
                              checked={selectedReports.includes(report.id)}
                              handleInput={(value, checked) => handleCheckboxChange(report.id)}
                            />
                            {new Date(report.created_at).toLocaleDateString('uk-UA', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </td>
                          <td>D</td>
                          <td>{report.team_id}</td>
                          <td>{report.clearance_task__internal_id}</td>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
          <div className="buttons-group">
            <ButtonComponent
              iconLeft={<CancelSvg />}
              className="danger"
              handleButton={handleClose}
              label="Cancel"
            />
            <ButtonComponent
              iconLeft={<SendSvg />}
              handleButton={handleCreate}
              label="Create"
              disabled={!weeklyCreateButtonEnabled}
            />
          </div>
        </div>
      </Dialog>
      {loader && <Loader visible={loader} />}
    </>
  );
};

export default CreateReportPopup;
